import { useEffect, useState } from "react";
import { API, getWithRetry } from "@/lib/constants";

// Per-asset daily transaction series for the trend sparklines.
//
// The only per-asset source is /assets/{id}/transactions (each tx carries a
// real block_time). It is slow and rate-limited, and times out (503) for very
// high-volume assets, so we fetch LAZILY with concurrency 1 + a gap between
// calls to protect the shared ~10 req/window budget, cache every result, and
// fall back gracefully (the row keeps its 2-point prev→now spark) whenever the
// endpoint fails or the fetched window lacks enough day-to-day spread.

const DAY = 86400;
const DAYS = 14;

// asset -> number[] (series) | null (unavailable) | undefined (loading/queued)
const cache = new Map();
const subs = new Map();
const queue = [];
let active = 0;
const MAX = 1;
const GAP_MS = 350;

const notify = (asset) => (subs.get(asset) || new Set()).forEach((fn) => fn(cache.get(asset)));

const buildDaily = (items) => {
  const times = (items || []).map((t) => Number(t.block_time)).filter((n) => Number.isFinite(n) && n > 0);
  if (times.length < 6) return null;
  const todayStart = Math.floor(Date.now() / 1000 / DAY) * DAY;
  const buckets = new Array(DAYS).fill(0);
  times.forEach((t) => {
    const idx = Math.round((todayStart - Math.floor(t / DAY) * DAY) / DAY); // 0 = today
    if (idx >= 0 && idx < DAYS) buckets[DAYS - 1 - idx] += 1; // oldest -> newest
  });
  // Need real spread across days, otherwise a single spike is misleading.
  if (buckets.filter((b) => b > 0).length < 3) return null;
  return buckets;
};

const pump = () => {
  if (active >= MAX || !queue.length) return;
  const asset = queue.shift();
  active += 1;
  getWithRetry(`${API}/assets/${asset}/transactions`, { params: { count: 100, order: "desc" }, timeout: 8000 }, { retries: 0 })
    .then((res) => { cache.set(asset, buildDaily(res.data?.items || res.data)); })
    .catch(() => { cache.set(asset, null); })
    .finally(() => { active -= 1; notify(asset); setTimeout(pump, GAP_MS); });
};

export const requestDailySeries = (asset) => {
  if (!asset || cache.has(asset)) return;
  cache.set(asset, undefined); // mark queued so we never double-fetch
  queue.push(asset);
  pump();
};

// undefined = loading, null = unavailable (keep fallback), array = daily counts
export const useAssetDailySeries = (asset, enabled = true) => {
  const [series, setSeries] = useState(() => cache.get(asset));
  useEffect(() => {
    if (!enabled || !asset) return undefined;
    setSeries(cache.get(asset));
    let set = subs.get(asset);
    if (!set) { set = new Set(); subs.set(asset, set); }
    set.add(setSeries);
    requestDailySeries(asset);
    return () => set.delete(setSeries);
  }, [asset, enabled]);
  return series;
};
